import type { WebinarRoomPollInputDto, WebinarRoomPollViewDto } from '@/api/webinarRoom'
import { DEFAULT_POLL_STAYS_UP_SECONDS, stayText } from '@/webinarRoom/polls/pollStay'

/**
 * What the Room tab's Polls card decides (ORB-1088): poll times as mm:ss, where the timeline ends, which polls
 * share a marker, and what a poll near another one does to it. The limits mirror
 * `api/OrbitalX.Business/Constants/WebinarRoom/WebinarRoomLimits.cs`, which the API checks again on save.
 */

/** `WebinarRoomLimits.PollQuestionMaxLength`. */
export const POLL_QUESTION_MAX_LENGTH = 200
/** `WebinarRoomLimits.PollOptionMaxLength`. */
export const POLL_OPTION_MAX_LENGTH = 120
/** `WebinarRoomLimits.PollMinOptions`. */
export const POLL_MIN_OPTIONS = 2
/** `WebinarRoomLimits.PollMaxOptions`. */
export const POLL_MAX_OPTIONS = 4
/** `WebinarRoomLimits.PollMaxOffsetSeconds`: twelve hours, the longest a session runs. */
export const POLL_MAX_OFFSET_SECONDS = 43200

/**
 * Only one poll shows at a time, so polls closer than this share a marker, and the later cuts the earlier short when
 * the earlier is still up when it pops up.
 */
export const POLL_PROXIMITY_SECONDS = 60
const TICK_SECONDS = 15 * 60
/** A last tick this close to the end, as a share of the length, would crowd the end's own label. */
const LAST_TICK_CROWDING = 0.09

const pad = (value: number) => String(value).padStart(2, '0')

/** "05:00", "18:30", "75:00": the minutes are padded but not capped. */
export function formatPollTime(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds))
    return `${pad(Math.floor(whole / 60))}:${pad(whole % 60)}`
}

/** Seconds from "18:30" (one to three digits of minutes), or null when it is not mm:ss. */
export function parsePollTime(text: string): number | null {
    const match = /^\s*(\d{1,3}):([0-5]\d)\s*$/.exec(text)
    return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

export function secondsText(seconds: number): string {
    return `${seconds} second${seconds === 1 ? '' : 's'}`
}

export interface PollTimeline {
    /** The timeline's length: the recording once one is ready, else the session. */
    lengthSeconds: number
    /** A poll at or after this never pops up. */
    limitSeconds: number
    /** What the limit is: the recording ending before the session, or the session ending first. */
    limitEnds: 'recording' | 'session'
    /** Where the session ends on a timeline that runs longer, to hatch what lies past it. Null otherwise. */
    sessionEndSeconds: number | null
    basis: 'recording' | 'session'
}

/**
 * The timeline for a session of `sessionSeconds` with a ready recording of `recordingSeconds`; either may be
 * unknown. Null when neither is known, as before the web has times or a recording.
 */
export function pollTimeline(sessionSeconds: number | null, recordingSeconds: number | null): PollTimeline | null {
    if (recordingSeconds !== null) {
        const recordingEndsFirst = sessionSeconds === null || recordingSeconds <= sessionSeconds
        const limit = recordingEndsFirst ? recordingSeconds : sessionSeconds!
        return {
            lengthSeconds: Math.max(recordingSeconds, 1),
            // A fractional end still lets a poll at its last whole second pop up.
            limitSeconds: Math.ceil(limit),
            limitEnds: recordingEndsFirst ? 'recording' : 'session',
            sessionEndSeconds: recordingEndsFirst ? null : sessionSeconds,
            basis: 'recording',
        }
    }
    if (sessionSeconds === null) return null
    return { lengthSeconds: sessionSeconds, limitSeconds: Math.ceil(sessionSeconds), limitEnds: 'session', sessionEndSeconds: null, basis: 'session' }
}

/** A tick every fifteen minutes from 0, then the end. */
export function timelineTicks(lengthSeconds: number): number[] {
    const ticks: number[] = []
    for (let at = 0; at < lengthSeconds; at += TICK_SECONDS) ticks.push(at)
    if (ticks.length > 1 && lengthSeconds - ticks[ticks.length - 1] < lengthSeconds * LAST_TICK_CROWDING) ticks.pop()
    ticks.push(lengthSeconds)
    return ticks
}

export function sortPolls<T extends Pick<WebinarRoomPollViewDto, 'OffsetSeconds'>>(polls: readonly T[]): T[] {
    return [...polls].sort((a, b) => a.OffsetSeconds - b.OffsetSeconds)
}

/** Polls less than a minute after the previous one in the group share its marker. Expects them sorted. */
export function clusterPolls<T extends Pick<WebinarRoomPollViewDto, 'OffsetSeconds'>>(sorted: readonly T[]): T[][] {
    const clusters: T[][] = []
    for (const poll of sorted) {
        const last = clusters[clusters.length - 1]
        if (last && poll.OffsetSeconds - last[last.length - 1].OffsetSeconds < POLL_PROXIMITY_SECONDS) last.push(poll)
        else clusters.push([poll])
    }
    return clusters
}

export interface PollNote {
    tone: 'error' | 'warning'
    text: string
}

type PollTiming = Pick<WebinarRoomPollViewDto, 'OffsetSeconds' | 'StaysUpSeconds'>

/** Whether a poll is still up at `atSeconds`: one with no stay stays up until the next poll replaces it. */
function stillUpAt(poll: PollTiming, atSeconds: number): boolean {
    return poll.StaysUpSeconds == null || poll.OffsetSeconds + poll.StaysUpSeconds > atSeconds
}

/** The next poll cuts this one short: it pops up within the minute while this one is still up. */
function replacedBy(poll: PollTiming, next: Pick<WebinarRoomPollViewDto, 'OffsetSeconds'>): boolean {
    return next.OffsetSeconds - poll.OffsetSeconds < POLL_PROXIMITY_SECONDS && stillUpAt(poll, next.OffsetSeconds)
}

/** "Stays up 1 minute", or "Stays up until the next poll", for a poll's row. */
export function staysUpRowText(staysUpSeconds: number | null | undefined): string {
    return staysUpSeconds == null ? 'Stays up until the next poll' : `Stays up ${stayText(staysUpSeconds)}`
}

const endsWord = (timeline: PollTimeline) => (timeline.limitEnds === 'recording' ? 'recording ends' : 'session ends')

/** What a poll's row warns about: being past the end, or being replaced by the next poll moments later while still up. */
export function pollRowNote(
    poll: PollTiming,
    next: Pick<WebinarRoomPollViewDto, 'OffsetSeconds'> | undefined,
    timeline: PollTimeline | null,
): PollNote | null {
    if (timeline && poll.OffsetSeconds >= timeline.limitSeconds) {
        return { tone: 'error', text: `This is after the ${endsWord(timeline)} at ${formatPollTime(timeline.limitSeconds)}, so it won’t pop up.` }
    }
    if (next && replacedBy(poll, next)) {
        return { tone: 'warning', text: `The ${formatPollTime(next.OffsetSeconds)} poll replaces it ${secondsText(next.OffsetSeconds - poll.OffsetSeconds)} later.` }
    }
    return null
}

/** The time field's limit, as its error says it. */
export function pollLimitMessage(timeline: PollTimeline): string {
    return `Before ${formatPollTime(timeline.limitSeconds)}, when the ${endsWord(timeline)}`
}

/** The poll form as typed. */
export interface PollDraft {
    time: string
    question: string
    options: string[]
    /** Null stays up until the next poll. */
    staysUpSeconds: number | null
}

export interface PollDraftCheck {
    offsetSeconds: number | null
    timeError: string | null
    questionError: string | null
    optionsError: string | null
    /** Another poll so close that one cuts the other short. Saving is still allowed. */
    warning: string | null
    ok: boolean
}

/** Checks a draft against the timeline and the web's other polls (`others` leaves out the poll being edited). */
export function checkPollDraft(draft: PollDraft, others: readonly PollTiming[], timeline: PollTimeline | null): PollDraftCheck {
    const offsetSeconds = parsePollTime(draft.time)
    let timeError: string | null = null
    if (offsetSeconds === null) timeError = 'Use mm:ss, like 18:30'
    else if (timeline && offsetSeconds >= timeline.limitSeconds) timeError = pollLimitMessage(timeline)
    else if (offsetSeconds > POLL_MAX_OFFSET_SECONDS) timeError = `Use a time up to ${formatPollTime(POLL_MAX_OFFSET_SECONDS)}`
    const questionError = draft.question.trim() ? null : 'Add the question.'
    const optionsError = draft.options.filter(option => option.trim()).length >= POLL_MIN_OPTIONS ? null : 'Add at least two options.'

    let warning: string | null = null
    if (offsetSeconds !== null && !timeError) {
        const draftTiming: PollTiming = { OffsetSeconds: offsetSeconds, StaysUpSeconds: draft.staysUpSeconds }
        // Only the poll just before can still be up when this one pops up, since any poll between replaced it, and only
        // the poll just after can cut this one short.
        const sorted = sortPolls(others)
        const same = sorted.find(other => other.OffsetSeconds === offsetSeconds)
        const earlier = sorted.filter(other => other.OffsetSeconds < offsetSeconds)
        const previous = earlier[earlier.length - 1]
        const next = sorted.find(other => other.OffsetSeconds > offsetSeconds)
        const nearest = same ?? [
            previous && replacedBy(previous, draftTiming) ? previous : undefined,
            next && replacedBy(draftTiming, next) ? next : undefined,
        ]
            .filter((other): other is PollTiming => other !== undefined)
            .sort((a, b) => Math.abs(a.OffsetSeconds - offsetSeconds) - Math.abs(b.OffsetSeconds - offsetSeconds))[0]
        if (nearest) {
            const gap = Math.abs(nearest.OffsetSeconds - offsetSeconds)
            const at = formatPollTime(nearest.OffsetSeconds)
            warning = gap === 0
                ? `Another poll already pops up at ${at}. Only one shows at a time, so one of them replaces the other straight away.`
                : nearest.OffsetSeconds < offsetSeconds
                    ? `The ${at} poll is only ${secondsText(gap)} earlier. This one replaces it while viewers may still be answering.`
                    : `The ${at} poll replaces this one ${secondsText(gap)} after it pops up, before most viewers have answered.`
        }
    }
    return { offsetSeconds, timeError, questionError, optionsError, warning, ok: !timeError && !questionError && !optionsError }
}

/**
 * The note a last poll left on "Until the next poll" gets: nothing replaces it, so it stays up to the end. Null for any
 * other draft, or while its time isn't usable.
 */
export function lastPollNote(draft: PollDraft, check: PollDraftCheck, others: readonly PollTiming[], sessionSeconds: number | null): string | null {
    const offsetSeconds = check.offsetSeconds
    if (draft.staysUpSeconds !== null || offsetSeconds === null || check.timeError) return null
    if (others.some(other => other.OffsetSeconds >= offsetSeconds)) return null
    const end = sessionSeconds === null ? '' : ` at ${formatPollTime(sessionSeconds)}`
    return `No poll comes after this one, so it stays up through the live Q&A until the session ends${end}.`
}

/** A blank draft, staying up a minute, or a poll's own values to edit. */
export function draftOf(poll?: WebinarRoomPollViewDto): PollDraft {
    return poll
        ? { time: formatPollTime(poll.OffsetSeconds), question: poll.Question, options: [...poll.Options], staysUpSeconds: poll.StaysUpSeconds ?? null }
        : { time: '', question: '', options: ['', ''], staysUpSeconds: DEFAULT_POLL_STAYS_UP_SECONDS }
}

/** Whether a draft says anything different from where it started. */
export function draftChanged(draft: PollDraft, initial: PollDraft): boolean {
    return draft.time.trim() !== initial.time.trim()
        || draft.question.trim() !== initial.question.trim()
        || draft.staysUpSeconds !== initial.staysUpSeconds
        || JSON.stringify(draft.options.map(option => option.trim())) !== JSON.stringify(initial.options.map(option => option.trim()))
}

/** A checked draft as the poll it saves: trimmed, blank options dropped. Throws on a draft that does not check out. */
export function pollFromDraft(draft: PollDraft, check: PollDraftCheck): Omit<WebinarRoomPollInputDto, 'Id'> {
    if (!check.ok || check.offsetSeconds === null) throw new Error('A poll is saved only from a draft that checks out')
    return {
        Question: draft.question.trim(),
        Options: draft.options.map(option => option.trim()).filter(Boolean),
        OffsetSeconds: check.offsetSeconds,
        StaysUpSeconds: draft.staysUpSeconds,
    }
}

/**
 * One change to the poll list, said as what the person meant, so it can be applied again to a newer list when
 * the save meets a 412.
 */
export type PollChange =
    | { kind: 'add'; poll: Omit<WebinarRoomPollInputDto, 'Id'> }
    | { kind: 'edit'; id: string; poll: Omit<WebinarRoomPollInputDto, 'Id'> }
    | { kind: 'delete'; id: string }

/**
 * The whole list to save after `change`. A poll without an id is new, and the API gives it one, which is also how
 * an undone delete comes back. An edit to a poll someone else has since deleted puts it back as new.
 */
export function applyPollChange(polls: readonly WebinarRoomPollViewDto[], change: PollChange): WebinarRoomPollInputDto[] {
    const inputs: WebinarRoomPollInputDto[] = polls.map(poll => ({
        Id: poll.Id, Question: poll.Question, Options: [...poll.Options], OffsetSeconds: poll.OffsetSeconds, StaysUpSeconds: poll.StaysUpSeconds ?? null,
    }))
    switch (change.kind) {
        case 'add':
            return [...inputs, { Id: null, ...change.poll }]
        case 'edit':
            return inputs.some(poll => poll.Id === change.id)
                ? inputs.map(poll => (poll.Id === change.id ? { Id: change.id, ...change.poll } : poll))
                : [...inputs, { Id: null, ...change.poll }]
        case 'delete':
            return inputs.filter(poll => poll.Id !== change.id)
    }
}

/** The saved poll a change produced, found by its contents because a new poll's id is the API's to give. */
export function findSavedPoll(polls: readonly WebinarRoomPollViewDto[], change: PollChange): WebinarRoomPollViewDto | undefined {
    if (change.kind === 'delete') return undefined
    if (change.kind === 'edit') {
        const same = polls.find(poll => poll.Id === change.id)
        if (same) return same
    }
    return polls.find(poll => poll.OffsetSeconds === change.poll.OffsetSeconds && poll.Question === change.poll.Question)
}
